import { Injectable, NgZone } from '@angular/core';
import { WebAPIToken } from '@models/webAPIToken.model';
import { Observable, ReplaySubject, from, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LoggingService } from '@services/logging.service';
import { AuthService } from './auth.service';
import { LogoutReasonCode } from '@shared/_constants';
import * as _ from 'lodash-es';
import moment from 'moment';

const TOKEN_REFRESH_CHECK_INTERVAL_MINUTES = 1;
const TOKEN_EXPIRY_DATE_MINUTES = 20; // this has to be in line with azure settings
const TOKEN_REFRESH_SHEDULE_MINUTES = TOKEN_EXPIRY_DATE_MINUTES - 10; // after 10 minutes the refresh token will be refreshed and procedure will repeat 10 times

@Injectable()
export class WebAPIService {
  private webAPIToken: WebAPIToken;

  private webApiToken$: ReplaySubject<WebAPIToken> = new ReplaySubject<WebAPIToken>(1);

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private logger: LoggingService,
    private ngZone: NgZone,
  ) {
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => {
        const currentDate = moment(new Date());
        const timeToRefresh = moment(new Date(this.webAPIToken.expiration)).subtract(TOKEN_REFRESH_SHEDULE_MINUTES, 'minutes');
        if (currentDate.isAfter(timeToRefresh)) {
          this.refreshWebAPIToken();
        }
      }, 60000 * TOKEN_REFRESH_CHECK_INTERVAL_MINUTES);
    });

    this.authService.configured.subscribe(() => {
      if (this.authService.hasValidAzureADAccessToken()) {
        this.getWebAPIToken().then((wat) => {
          this.webApiToken$.next(wat);
          this.webApiToken$.complete();
        });
      }
    });

    this.authService.tokenReceived.subscribe(() => {
      this.getWebAPIToken().then((wat) => {
        this.webApiToken$.next(wat);
        this.webApiToken$.complete();
      });
    });
  }

  public async hasValidWebAPIAccessToken(): Promise<boolean> {
    this.logger.info(this.constructor.name, 'hasValidWebAPIAccessToken', 'Start');
    try {
      let validToken = false;
      await this.getWebAPITokenForCurrentUser();

      if (this.webAPIToken) {
        validToken = true;
      }

      this.logger.info(this.constructor.name, 'hasValidWebAPIAccessToken', `validToken:[${validToken}]`);
      return validToken;
    } finally {
      this.logger.info(this.constructor.name, 'hasValidWebAPIAccessToken', 'End');
    }
  }

  public getObservableWebAPITokenForCurrentUser(): Observable<WebAPIToken> {
    this.logger.info(this.constructor.name, 'getObservableWebAPITokenForCurrentUser', 'Start');
    try {
      // https://medium.com/@benlesh/rxjs-observable-interop-with-promises-and-async-await-bebb05306875
      return from(this.getWebAPITokenForCurrentUser());
    } finally {
      this.logger.info(this.constructor.name, 'getObservableWebAPITokenForCurrentUser', 'End');
    }
  }

  private async getWebAPITokenForCurrentUser(): Promise<WebAPIToken> {
    this.logger.info(this.constructor.name, 'getWebAPITokenForCurrentUser', 'Start');
    try {
      if (this.webAPIToken) {
        return this.webAPIToken;
      } else {
        this.logger.info(this.constructor.name, 'getWebAPITokenForCurrentUser', 'No token; get one');
        return await this.getWebAPIToken(); // this.webApiToken$.toPromise();
      }
    } finally {
      this.logger.info(this.constructor.name, 'getWebAPITokenForCurrentUser', 'End');
    }
  }

  private async getWebAPIToken(): Promise<WebAPIToken> {
    this.logger.info(this.constructor.name, 'getWebAPIToken', 'Start');
    try {
      const claimUPN = this.authService.getClaimUPN();

      if (claimUPN) {
        const graphAccessToken = await this.authService.getAzureADAccessToken();
        this.webAPIToken = <WebAPIToken>await firstValueFrom(
          this.http.post(
            '/api/auth/token',
            { EmailAddress: claimUPN },
            {
              headers: { Authorization: `Bearer ${graphAccessToken}` },
            },
          ),
        ).catch((e) => this.logger.httpError(this.constructor.name, 'getWebAPIToken', e, claimUPN));
      }
      return this.webAPIToken;
    } finally {
      this.logger.info(this.constructor.name, 'getWebAPIToken', 'End');
    }
  }

  private async refreshWebAPIToken(): Promise<WebAPIToken> {
    this.logger.info(this.constructor.name, 'refreshWebAPIToken', 'Start');
    try {
      const claimUPN = this.authService.getClaimUPN();

      // In case of any exception from RefreshToken API call token should be preserved.
      //sometimes its throwing SQL timeout exception and token is getting wiped out and user is unable to anything after that
      const copyToken = _.cloneDeep(this.webAPIToken);

      this.webAPIToken = <WebAPIToken>await firstValueFrom(
        this.http.post('/api/auth/refreshtoken', { EmailAddress: claimUPN, RefreshToken: this.webAPIToken.refreshToken }),
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'refreshWebAPIToken', e, claimUPN);
        if (e.status === 401) {
          // when refresh returns us a 401 it's telling us that our refresh token has expired
          this.logger.error(this.constructor.name, 'refreshWebAPIToken', `Refresh Token expired need to send to logged out`);
          this.authService.logOut(LogoutReasonCode.webapiRefreshTokenExpired);
          // this.router.navigate(['/nostudyaccess']); // absolute path since we may be in a child route
        }
      });

      if (this.webAPIToken === undefined || this.webAPIToken === null) {
        if (copyToken === undefined || copyToken === null) {
          this.logger.warn(this.constructor.name, 'refreshWebAPIToken', `webAPIToken:[${this.webAPIToken}]`);
        } else {
          this.webAPIToken = copyToken;
        }
      }
      return this.webAPIToken;
    } finally {
      this.logger.info(this.constructor.name, 'refreshWebAPIToken', 'End');
    }
  }
}
